<script setup>
    /**
     * Vue Scripts
     */
    import {computed, ref, watch} from "vue";
    import {toNumber} from "lodash";
    import {trialStore} from "@pinia/trial-store";
    import {generateSvgFromString, scrollToElement} from "@util/mixins";
    import {findLocationIndex, handlePinSelection, resetAllPins} from "@util/map";

    /**
     * Vue Components
     */
    import {AdvancedMarker, GoogleMap} from 'vue3-google-map'

    const store = trialStore()

    const {langCode} = GLOBAL;
    const {apiKey} = TRIAL;
    const {zoomLevel} = LOC?.mapSettings;
    const {home, pin, pinSelected} = store.icons

    const mapRef = ref(null)

    const center = computed(() => ({
        lat: store.query.lat,
        lng: store.query.lng,
    }))
    const locationName = computed(() => store.locationName)
    const selectedLocation = computed(() => toNumber(store.locationSelected))
    const markers = computed(() => store.markers)

    watch([() => mapRef.value?.ready, markers], ([ready, markers]) => {
        if (!ready)
            return

        const {api, map} = mapRef.value;
        if (api) {
            const bounds = new api.LatLngBounds();
            // Add the home or searched location to the bounds
            bounds.extend(center.value)
            if (markers.length) {
                markers
                    .forEach(marker => bounds.extend(marker.position))
                map.fitBounds(bounds);
            }
        }
    })

    /**
     * Handles the events when the marker is selected, locating
     * the pin in the marker's array, returning its index and
     * setting the Listing Location with the selected class
     * to scroll down to
     *
     * @param marker
     */
    const markerClickHandler = (marker) => {
        const {srcElement} = marker?.domEvent;
        if (!srcElement) return;

        const markerPinParent = srcElement.closest('[class*="-marker-view"]');
        if (!markerPinParent) return;

        const locationIndex = findLocationIndex(markers, markerPinParent.title);

        if (selectedLocation.value === locationIndex) return;

        const locationsListing = document.getElementById('locations-listing');
        if (!locationsListing) return;

        resetAllPins(pin);
        handlePinSelection(markerPinParent, pinSelected)
        store.updateLocationSelected(locationIndex)

        const locationSelected = locationsListing.querySelector(`[data-facility="${markerPinParent.title}"]`);
        if (!locationSelected) return
        // Scroll to the selected location in the listing box
        scrollToElement(locationSelected, locationsListing, 20);

    }
</script>

<template>
    <div class="map-wrapper">
        <GoogleMap
            ref="mapRef"
            class="mapdiv__container"

            :apiKey="apiKey"

            :center="center"
            :fullscreenControl="false"
            :language="langCode"
            :mapTypeControl="false"
            :scaleControl="false"
            :streetViewControl="false"
            :zoom="toNumber(zoomLevel)"

            gestureHandling="cooperative"
            mapId="location-map"
        >

            <!-- Center Position Marker -->
            <AdvancedMarker
                :options="{
                    content: generateSvgFromString(home),
                    position: center,
                    title: locationName || 'Home',
                    zIndex: 9999,
                }"
            />
            <AdvancedMarker
                v-if="markers.length"
                v-for="({index, position, title}) in markers"
                :key="index"
                :options="{
                    content: generateSvgFromString(pin),
                    position: position,
                    title: title,
                    zIndex: index,
                }"
                @click="markerClickHandler"
            />
        </GoogleMap>
    </div>
</template>

<script>
    export default {
        name: "Map"
    }
</script>

<style scoped lang="scss">

</style>
